import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AdminShell } from "@/platform/layout/admin-shell";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { fetchAdminMetrics, fetchAdminMrrHistory } from "@/platform/admin/api";
import { downloadCsv } from "@/platform/lib/csv";

type Range = 7 | 30 | 90;

export function AdminMetricsPage() {
  const [range, setRange] = useState<Range>(30);
  const [mrrRange, setMrrRange] = useState<30 | 90 | 180>(90);
  const query = useQuery({
    queryKey: ["admin", "metrics", range],
    queryFn: () => fetchAdminMetrics(range),
    staleTime: 60_000,
  });
  const mrrQuery = useQuery({
    queryKey: ["admin", "mrr-history", mrrRange],
    queryFn: () => fetchAdminMrrHistory(mrrRange),
    staleTime: 60_000,
  });

  return (
    <AdminShell
      title="Métricas"
      description={`Receita recorrente, conversão de trial, churn e consumo LLM dos últimos ${range} dias.`}
      actions={
        <div className="inline-flex rounded-full border border-[--color-border] bg-[color:var(--color-card)] p-1">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setRange(d)}
              className={
                range === d
                  ? "rounded-full bg-[color:var(--color-accent)] px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium text-white"
                  : "rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]"
              }
              aria-pressed={range === d}
            >
              {d}d
            </button>
          ))}
        </div>
      }
    >
      {query.isLoading ? (
        <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
      ) : query.error || !query.data ? (
        <p className="text-[color:var(--color-danger)]">Falha ao carregar métricas.</p>
      ) : (
        <>
          {/* KPIs de período */}
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Conversão trial → pago"
              value={`${(query.data.conversionRate.rate * 100).toFixed(1)}%`}
              hint={`${query.data.conversionRate.convertedToActive30d} de ${query.data.conversionRate.trialsStarted30d} trials`}
            />
            <Kpi
              label="Churn (30 dias)"
              value={`${(query.data.churnRate30d * 100).toFixed(1)}%`}
              hint={query.data.churnRate30d > 0.05 ? "Acima de 5%, atenção" : "Saudável"}
            />
            <Kpi
              label="MRR total"
              value={`R$ ${planTotal(query.data.planBreakdown).toLocaleString("pt-BR")}`}
              hint={`${query.data.planBreakdown.reduce((a, p) => a + p.count, 0)} assinaturas ativas`}
            />
            <Kpi
              label="LLM custo estimado (30d)"
              value={`US$ ${query.data.topUsersByLlm30d
                .reduce((a, u) => a + u.cost_estimate, 0)
                .toFixed(2)}`}
              hint="Estimativa por modelo (Pro $0.05, Flash $0.002 por chamada)"
            />
          </section>

          {/* Signups por dia */}
          <section className="mt-8 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Novas usuárias por dia
            </h3>
            <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              Últimos 30 dias.
            </p>
            <div className="mt-4" style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={query.data.signupsByDay}>
                  <CartesianGrid stroke="var(--color-border)" strokeDasharray="2 2" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                  />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="count" fill="var(--color-accent)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Assinaturas por dia */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Movimentação de assinaturas
            </h3>
            <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              Novas ativas, novas em trial e canceladas por dia.
            </p>
            <div className="mt-4" style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={query.data.subsByDay}>
                  <CartesianGrid stroke="var(--color-border)" strokeDasharray="2 2" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                  />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="active"
                    stroke="var(--color-success)"
                    strokeWidth={2}
                    name="Ativas"
                  />
                  <Line
                    type="monotone"
                    dataKey="trialing"
                    stroke="var(--color-info)"
                    strokeWidth={2}
                    name="Trials"
                  />
                  <Line
                    type="monotone"
                    dataKey="canceled"
                    stroke="var(--color-danger)"
                    strokeWidth={2}
                    name="Canceladas"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* MRR histórico */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
                  Receita recorrente (MRR) histórica
                </h3>
                <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  Soma diária dos planos ativos, separada por tenant. Reflete crescimento ou contração de receita ao longo do tempo.
                </p>
              </div>
              <div className="inline-flex rounded-full border border-[--color-border] bg-[color:var(--color-card)] p-1">
                {([30, 90, 180] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setMrrRange(d)}
                    className={
                      mrrRange === d
                        ? "rounded-full bg-[color:var(--color-accent)] px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium text-white"
                        : "rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]"
                    }
                    aria-pressed={mrrRange === d}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4" style={{ width: "100%", height: 280 }}>
              {mrrQuery.isLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
                </div>
              ) : mrrQuery.data && mrrQuery.data.series.length > 0 ? (
                <ResponsiveContainer>
                  <AreaChart data={mrrQuery.data.series}>
                    <defs>
                      <linearGradient id="mrrEinstein" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="mrrUsp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-info)" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="var(--color-info)" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--color-border)" strokeDasharray="2 2" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => d.slice(5)}
                      stroke="var(--color-muted-foreground)"
                      fontSize={11}
                      minTickGap={20}
                    />
                    <YAxis
                      stroke="var(--color-muted-foreground)"
                      fontSize={11}
                      tickFormatter={(v) =>
                        v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--color-card)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: number) =>
                        `R$ ${value.toLocaleString("pt-BR")}`
                      }
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="einsteinBrl"
                      stroke="var(--color-accent)"
                      fill="url(#mrrEinstein)"
                      strokeWidth={2}
                      name="Einstein"
                      stackId="mrr"
                    />
                    <Area
                      type="monotone"
                      dataKey="uspBrl"
                      stroke="var(--color-info)"
                      fill="url(#mrrUsp)"
                      strokeWidth={2}
                      name="USP"
                      stackId="mrr"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  Sem dados de receita ainda.
                </p>
              )}
            </div>
            {mrrQuery.data && mrrQuery.data.series.length > 0 ? (
              <p className="mt-3 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                MRR atual:{" "}
                <span className="tabular-nums font-medium text-[color:var(--color-foreground)]">
                  R${" "}
                  {mrrQuery.data.series[
                    mrrQuery.data.series.length - 1
                  ]!.totalBrl.toLocaleString("pt-BR")}
                </span>
                {" · "}
                {(() => {
                  const last = mrrQuery.data.series[mrrQuery.data.series.length - 1]!;
                  const first = mrrQuery.data.series[0]!;
                  const delta = last.totalBrl - first.totalBrl;
                  const pct = first.totalBrl > 0 ? (delta / first.totalBrl) * 100 : 0;
                  return (
                    <span
                      className={
                        delta >= 0
                          ? "text-[color:var(--color-success)]"
                          : "text-[color:var(--color-danger)]"
                      }
                    >
                      {delta >= 0 ? "+" : ""}
                      R$ {delta.toLocaleString("pt-BR")} ({pct >= 0 ? "+" : ""}
                      {pct.toFixed(1)}%) em {mrrRange} dias
                    </span>
                  );
                })()}
              </p>
            ) : null}
          </section>

          {/* Plan breakdown */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Distribuição por plano
            </h3>
            <ul className="mt-4 space-y-2">
              {query.data.planBreakdown.map((p) => (
                <li
                  key={`${p.tenant}-${p.plan}`}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-4 rounded-xl border border-[--color-border] bg-[color:var(--color-paper)] p-4 text-[length:var(--text-body-sm)]"
                >
                  <span className="capitalize">
                    {p.tenant} · {p.plan}
                  </span>
                  <Badge variant="outline">{p.count} ativas</Badge>
                  <span className="tabular-nums font-medium">
                    R$ {p.mrrBrl.toLocaleString("pt-BR")}/mês
                  </span>
                </li>
              ))}
              {query.data.planBreakdown.length === 0 ? (
                <li className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  Nenhum plano ativo ainda.
                </li>
              ) : null}
            </ul>
          </section>

          {/* Top users LLM */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
                  Top 10 consumo LLM ({range}d)
                </h3>
                <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  Útil para detectar uso anômalo ou abusivo antes do gasto Gemini sair do controle.
                </p>
              </div>
              {query.data.topUsersByLlm30d.length > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    downloadCsv(
                      `top_consumo_llm_${range}d_${new Date().toISOString().slice(0, 10)}.csv`,
                      query.data!.topUsersByLlm30d,
                      [
                        { key: "email", header: "Email" },
                        { key: "calls", header: "Chamadas" },
                        { key: "cost_estimate", header: "Custo estimado (USD)" },
                        { key: "user_id", header: "user_id" },
                      ],
                    );
                    toast.success("CSV gerado.");
                  }}
                >
                  <Download className="size-3.5" />
                  Exportar CSV
                </Button>
              ) : null}
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[length:var(--text-body-sm)]">
                <thead>
                  <tr className="text-left text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3 text-right">Chamadas</th>
                    <th className="py-2 pr-3 text-right">Custo estimado</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.topUsersByLlm30d.map((u) => (
                    <tr key={u.user_id} className="border-t border-[--color-border]">
                      <td className="py-2 pr-3">
                        <Link to={`/admin/usuarios/${u.user_id}`} className="hover:underline">
                          {u.email}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {u.calls.toLocaleString("pt-BR")}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        US$ {u.cost_estimate.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {query.data.topUsersByLlm30d.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-4 text-center text-[color:var(--color-muted-foreground)]">
                        Sem chamadas LLM nos últimos 30 dias.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </AdminShell>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)]">
      <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </p>
      <p
        data-tabular="true"
        className="mt-2 font-display text-[length:var(--text-display-sm)] font-semibold leading-none tabular-nums"
      >
        {value}
      </p>
      {hint ? (
        <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function planTotal(planBreakdown: Array<{ mrrBrl: number }>): number {
  return Math.round(planBreakdown.reduce((a, p) => a + p.mrrBrl, 0) * 100) / 100;
}
